import { useEffect, useState, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMaterialsPanelStore } from '../../stores/materialsPanelStore';
import { useGenerationAssets } from '../generation/state/assetsContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useUploadTemporaryMaterial, useUploadCloudMaterial } from '../../features/materials/hooks';
import { rasterErrorMessage, useEnsureMaterialRasterUrl } from '../../features/materials/ensureRasterAsset';
import { detectIsTemporary } from '../../features/materials/materialUrl';
import { useToast } from '../../hooks/useToast';
import { Button } from '../common/Button';
import { Tooltip } from '../common/Tooltip';
import SimpleMaterialsList, { type SimpleMaterialsListRef, MATERIALS_FILTER_CARRYOVER_KEY } from './SimpleMaterialsList';
import { UploadMaterialModal } from '../materials/UploadMaterialModal';
import { withSvgAccept } from '../../features/materials/svgLayerUpload';
import { useSvgLayerUploadGate } from '../../features/materials/useSvgLayerUploadAllowed';
import { brandConfig } from '../../config/brands';
import { hardNavigate } from '../../utils/navigation';
import { useUrlCaptureEntry } from '../../features/urlCapture/useUrlCaptureEntry';
import './RightPanelWrapper.css';
import { ThreeDotsIcon } from '../common/selectIcons';

// 素材パネルを表示しないページのパス（前方一致）
const HIDDEN_PANEL_PATHS = ['/onboarding', '/login', '/callback'];

// 素材パネルを表示しないページのパス（完全一致）
const HIDDEN_PANEL_EXACT_PATHS: string[] = [];

// RouterProvider外で動作するため、window.locationを使用してパスをチェック
function isHiddenPath(): boolean {
  const pathname = window.location.pathname;
  if (HIDDEN_PANEL_EXACT_PATHS.includes(pathname)) return true;
  return HIDDEN_PANEL_PATHS.some(path => pathname.startsWith(path));
}

const PANEL_WIDTH_STORAGE_KEY = 'right_panel_width_px';
const DEFAULT_PANEL_WIDTH = 600; // ピクセル
const MIN_PANEL_WIDTH = 372; // 2列の最小幅 (150*2 + 16 + 56)
const MAX_PANEL_WIDTH_RATIO = 0.8; // 画面幅の80%まで

export default function RightPanelWrapper() {
  const materialLabel = brandConfig.labels.material;
  // 簡素素材 UI (usodeki 等): 個人フォルダのみ・ピッカー先行アップロード
  const simpleMaterialsUi = brandConfig.features.simpleMaterialsUi ?? false;
  const { isOpen, close, mode, contextData, storyboardImageUrl, requestActiveTab, clearRequestActiveTab } = useMaterialsPanelStore();
  const { selectedAssets, addAsset, removeAsset, shouldOpenPersonalFolder, clearOpenPersonalFolderRequest } = useGenerationAssets();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const toast = useToast();
  const ensureMaterialRasterUrl = useEnsureMaterialRasterUrl();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  // usodeki: 「画像アップロード」タップで先にファイルピッカーを開き、選択後に
  // ファイル入りでアップロードモーダルを開く (空のモーダルを先に出さない)。
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  // SVG (レイヤー素材) はモーショングラフィックスのタブが見える利用者だけ。ここでは accept に含めるかだけ
  // (availability は取りに行かない)。受けない SVG は UploadMaterialModal の検査が「対応していない形式」で弾く
  const { acceptSvg: allowSvgUpload } = useSvgLayerUploadGate();
  const handleUploadPick = () => uploadInputRef.current?.click();
  const handleUploadInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingUploadFile(file);
      setUploadModalOpen(true);
    }
    e.target.value = '';
  };
  const [bulkEditMode, setBulkEditMode] = useState<'none' | 'delete' | 'tags' | 'move' | 'product-link'>('none');
  const [menuOpen, setMenuOpen] = useState(false);
  const [bulkEditMenuOpen, setBulkEditMenuOpen] = useState(false);
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  // URLキャプチャ (docs/features/swipe-motion-clip-plan.md §11.2.8): 生成キューのある画面だけ・簡素素材 UI では出さない
  const urlCapture = useUrlCaptureEntry();
  const showUrlCapture = urlCapture.available && !simpleMaterialsUi;
  // URLパスまたはパラメータからデフォルトのタブを設定
  // ストーリーボードタブは storyboardImageUrl が立っている間だけ第3タブとして出る。
  const [activeTab, setActiveTab] = useState<'cloud' | 'temporary' | 'storyboard'>(() => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      // 新URL構造: /materials/personal → temporary タブ
      if (path.startsWith('/materials/personal')) {
        return 'temporary';
      }
      // 新URL構造: /materials/shared → cloud タブ
      if (path.startsWith('/materials/shared')) {
        return 'cloud';
      }
      // 旧URL互換: folder_type パラメータ
      const params = new URLSearchParams(window.location.search);
      const folderType = params.get('folder_type');
      if (folderType === 'temporary') {
        return 'temporary';
      }
    }
    // 簡素素材 UI は共有フォルダを出さないため個人フォルダを既定にする
    if (brandConfig.features.simpleMaterialsUi) return 'temporary';
    return 'cloud';
  });

  // パネル幅（ピクセル）をLocalStorageから復元
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem(PANEL_WIDTH_STORAGE_KEY);
    if (saved) {
      const parsed = parseFloat(saved);
      const maxWidth = window.innerWidth * MAX_PANEL_WIDTH_RATIO;
      if (!isNaN(parsed) && parsed >= MIN_PANEL_WIDTH && parsed <= maxWidth) {
        return parsed;
      }
    }
    return DEFAULT_PANEL_WIDTH;
  });

  const [isClosing, setIsClosing] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isSnapping, setIsSnapping] = useState(false);
  const [actualPanelWidth, setActualPanelWidth] = useState(0); // 実際のピクセル幅
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const swipeStartYRef = useRef<number>(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const bulkEditMenuRef = useRef<HTMLDivElement>(null);
  const importMenuRef = useRef<HTMLDivElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const materialsListRef = useRef<SimpleMaterialsListRef>(null);
  const uploadTempMutation = useUploadTemporaryMaterial();
  const uploadCloudMutation = useUploadCloudMaterial();
  const isCameraUploading = uploadTempMutation.isPending || uploadCloudMutation.isPending;
  const panelRef = useRef<HTMLDivElement>(null);
  // BulkActionsBar の portal 先として React の更新を介して受け渡せるよう、要素を state でも保持する
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
  const handlePanelRef = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
    setPanelEl(el);
  }, []);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(panelWidth);

  // 特定のページでは素材パネルを表示しない（RouterProvider外なのでwindow.locationを使用）
  const shouldHidePanel = isHiddenPath();

  // パネルを閉じる処理（モバイルではスライドアウトアニメーション後にストア更新）
  const handleClose = useCallback(() => {
    if (isMobile) {
      setIsClosing(true);
    } else {
      close();
    }
  }, [isMobile, close]);

  // カメラ撮影後のアップロード処理
  const handleCameraCapture = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const payload = { file, source_type: 'camera' };
    const result = await uploadTempMutation.mutateAsync(payload);
    // アップロード成功後、選択中素材に自動追加
    if (result?.id) {
      const thumbnailUrl = result.thumbnail_url || result.preview_url || result.storage_path || '';
      addAsset({
        id: result.id,
        thumbnailUrl,
        type: 'temporary',
        storageBucket: result.storage_bucket ?? undefined,
        storagePath: result.storage_path ?? undefined,
        contentType: result.content_type,
        width: result.width,
        height: result.height,
      });
    }
    // inputをリセットして同じファイルを再選択可能に
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  }, [activeTab, uploadTempMutation, uploadCloudMutation, addAsset]);

  // モバイル: スワイプダウンで閉じるジェスチャー
  const handleSwipeStart = useCallback((e: React.TouchEvent) => {
    swipeStartYRef.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);

  const handleSwipeMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return;
    const deltaY = e.touches[0].clientY - swipeStartYRef.current;
    if (deltaY > 0) {
      setDragOffsetY(deltaY);
    }
  }, [isDragging]);

  const handleSwipeEnd = useCallback(() => {
    setIsDragging(false);
    if (dragOffsetY > 100) {
      handleClose();
    }
    setDragOffsetY(0);
  }, [dragOffsetY, handleClose]);

  // モバイルスライドアウト完了時の処理
  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    if (isClosing && e.propertyName === 'transform') {
      setIsClosing(false);
      close();
    }
  }, [isClosing, close]);

  // isOpenがfalseになったらisClosingもリセット（外部からcloseされた場合のフォールバック）
  useEffect(() => {
    if (!isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  // リサイズ開始
  const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    startXRef.current = clientX;
    startWidthRef.current = panelWidth;
  }, [panelWidth]);

  // 現在表示されている列数を基準に最適な幅（ピクセル）を計算してスナップ
  const snapToOptimalColumns = useCallback((currentWidthPx: number) => {
    const windowWidth = window.innerWidth;

    // カード幅とgapの定義
    const cardWidth = 150;
    const gap = 16;

    // パネル幅からグリッドclientWidthへの差分: divパディング24px×2 = 48px
    const panelToGridDiff = 48;
    // グリッドclientWidthからカード配置領域への差分: グリッドパディング4px×2 = 8px
    const gridPadding = 8;
    // パネル幅からカード配置領域への合計差分
    const widthDiff = panelToGridDiff + gridPadding; // 56px

    // 現在のパネル幅からカード配置領域を計算
    const gridContainerWidth = currentWidthPx - widthDiff;

    // 何列入るかを直接チェック（境界値の問題を回避）
    let targetColumns = 2; // 最小2列
    for (let n = 2; n <= 10; n++) {
      const requiredWidth = cardWidth * n + gap * (n - 1);
      if (gridContainerWidth >= requiredWidth) {
        targetColumns = n;
      } else {
        break;
      }
    }

    // 目標の列数に対する最適なグリッド幅を計算
    const optimalGridWidth = cardWidth * targetColumns + gap * (targetColumns - 1);
    // +1px: CSS Gridのauto-fill計算時の境界条件で、サブピクセルレンダリングや
    // 内部の丸め処理により、ぎりぎりの幅では列が作られないことがあるための安全マージン
    const optimalPanelWidth = optimalGridWidth + widthDiff + 1;
    const maxWidth = windowWidth * MAX_PANEL_WIDTH_RATIO;
    const clampedPanelWidth = Math.min(Math.max(optimalPanelWidth, MIN_PANEL_WIDTH), maxWidth);

    return clampedPanelWidth;
  }, []);

  // リサイズ中
  useEffect(() => {
    if (!isResizing) return;

    const handleMove = (clientX: number) => {
      const deltaX = startXRef.current - clientX;
      const maxWidth = window.innerWidth * MAX_PANEL_WIDTH_RATIO;
      const newWidth = Math.min(Math.max(startWidthRef.current + deltaX, MIN_PANEL_WIDTH), maxWidth);
      setPanelWidth(newWidth);
    };

    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const handleTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX);

    const handleEnd = () => {
      setIsResizing(false);

      // 最適な列数にスナップ
      const optimalWidth = snapToOptimalColumns(panelWidth);
      setIsSnapping(true);
      setPanelWidth(optimalWidth);

      // アニメーション終了後にスナッピング状態を解除
      setTimeout(() => {
        setIsSnapping(false);
      }, 300);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isResizing, panelWidth, snapToOptimalColumns]);

  // パネル幅をLocalStorageに保存
  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, panelWidth.toString());
  }, [panelWidth]);

  // パネルを開くたびに素材を再取得（閉じている間にinvalidateされたキャッシュを反映）
  useEffect(() => {
    if (isOpen) {
      queryClient.refetchQueries({ queryKey: [activeTab === 'cloud' ? 'cloud-materials' : 'temporary-materials'] });
    }
  }, [isOpen, activeTab, queryClient]);

  // 生成完了時に個人フォルダタブを開く
  useEffect(() => {
    if (shouldOpenPersonalFolder) {
      setActiveTab('temporary');
      clearOpenPersonalFolderRequest();

      // タブ切り替え後にスクロール処理を実行（少し遅延を入れてDOMの更新を待つ）
      setTimeout(() => {
        materialsListRef.current?.scrollToTop();
      }, 300);
    }
  }, [shouldOpenPersonalFolder, clearOpenPersonalFolderRequest]);

  // ストア経由のタブ切替リクエスト (openStoryboardPreview / closeStoryboardPreview 等) を消化
  useEffect(() => {
    if (!requestActiveTab) return;
    setActiveTab(requestActiveTab);
    clearRequestActiveTab();
  }, [requestActiveTab, clearRequestActiveTab]);

  // storyboardImageUrl が外部要因で消えた状態で activeTab が 'storyboard' のままなら、
  // 素材タブに戻して整合性を保つ。
  useEffect(() => {
    if (!storyboardImageUrl && activeTab === 'storyboard') {
      setActiveTab('cloud');
    }
  }, [storyboardImageUrl, activeTab]);

  // URLパス・パラメータの変更を監視してタブを切り替える
  // ブラウザの戻る/進むボタンやURLの変更時のみ反応する（タブクリック時は反応しない）
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkFolderType = () => {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);

      // 新URL構造: /materials/personal → temporary
      if (path.startsWith('/materials/personal')) {
        setActiveTab('temporary');
        return;
      }
      // 新URL構造: /materials/shared → cloud
      if (path.startsWith('/materials/shared')) {
        setActiveTab('cloud');
        return;
      }

      // 旧URL互換: folder_type パラメータ
      const folderType = params.get('folder_type');
      if (folderType === 'temporary') {
        setActiveTab('temporary');
      } else if (folderType === 'cloud') {
        setActiveTab('cloud');
      }
      // folder_typeパラメータがない場合は何もしない（現在のタブを維持）
    };

    // 初回チェックは行わない（タブの初期値はuseStateの初期化で設定済み）
    // checkFolderType();

    // popstateイベント（ブラウザの戻る/進むボタン）
    const handlePopState = () => {
      checkFolderType();
    };
    window.addEventListener('popstate', handlePopState);

    // pushState/replaceStateをフック（React Routerなどが使用）
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      checkFolderType();
    };

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      checkFolderType();
    };

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []); // 依存配列を空にして、マウント時のみ実行

  // 素材パネルの下に重ねるモーダル (.modal-overlay--beside-panel) が残り幅の中央に寄れるよう、
  // PC で開いている間だけパネル幅を CSS 変数で公開する (scene-frame-generation-tuning-plan.md §4.5)
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--materials-panel-width', isOpen && !isMobile ? `${panelWidth}px` : '0px');
    return () => { root.style.setProperty('--materials-panel-width', '0px'); };
  }, [isOpen, isMobile, panelWidth]);

  // Escキーで閉じる & bodyクラス管理 & スクロールロック
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);

      if (isMobile) {
        // スマホ時は背景スクロールを防止 & ヘッダー非表示
        document.body.style.overflow = 'hidden';
        document.body.classList.add('mobile-sheet-open');
      } else {
        document.body.classList.add('right-panel-open');
      }

      return () => {
        document.removeEventListener('keydown', handleEscape);
        if (isMobile) {
          document.body.style.overflow = '';
          document.body.classList.remove('mobile-sheet-open');
        } else {
          document.body.classList.remove('right-panel-open');
        }
      };
    }
  }, [isOpen, isMobile, handleClose]);

  // メニューの外側クリックでメニューを閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [menuOpen]);

  // 一括編集メニューの外側クリックでメニューを閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (bulkEditMenuRef.current && !bulkEditMenuRef.current.contains(event.target as Node)) {
        setBulkEditMenuOpen(false);
      }
    }

    if (bulkEditMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [bulkEditMenuOpen]);

  // 素材取込メニューの外側クリックでメニューを閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (importMenuRef.current && !importMenuRef.current.contains(event.target as Node)) {
        setImportMenuOpen(false);
      }
    }

    if (importMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [importMenuOpen]);

  // パネル幅をCSSカスタムプロパティとして設定（スマホ時は全画面モーダルなので不要）
  useEffect(() => {
    if (isOpen && !isMobile) {
      document.documentElement.style.setProperty('--panel-width', `${panelWidth}px`);
    } else {
      document.documentElement.style.removeProperty('--panel-width');
    }
  }, [isOpen, panelWidth, isMobile]);

  // パネル幅を監視
  useEffect(() => {
    if (!panelRef.current || !isOpen) {
      setActualPanelWidth(0);
      return;
    }

    const updateWidth = () => {
      if (panelRef.current) {
        setActualPanelWidth(panelRef.current.offsetWidth);
      }
    };

    // 初期幅を設定
    updateWidth();

    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    resizeObserver.observe(panelRef.current);
    return () => resizeObserver.disconnect();
  }, [isOpen]);

  // レスポンシブ表示の判定（各ボタン個別に制御）
  // 新レイアウト: [素材ページを開く][商品データ取込][素材アップロード][一括編集▼][ごみ箱] … [X]
  // - padding 24*2 = 48
  // - 素材ページを開く: 32 + 8 gap = 40
  // - 閉じる(X): 32 + 8 gap = 40
  // - 三点メニュー: 32 + 8 gap = 40（折り畳みが発生している時のみ表示）

  // padding + 素材ページを開く + 閉じる = 128
  const HEADER_BASE_WIDTH = 48 + 40 + 40;
  const MENU_BUTTON_WIDTH = 40;           // 三点メニュー + gap

  // 各ボタンの推定幅（+ gap）
  const BUTTON_WIDTHS = {
    import: 188,    // 商品データ取込
    upload: 148,    // 素材アップロード
    bulk: 130,      // 一括編集▼
    trash: 78,      // ごみ箱
  };

  // 右から順に折り畳む: ごみ箱 → 一括編集 → 素材アップロード → 商品データ取込
  // ごみ箱まで表示できる幅なら折り畳み不要（メニューは非表示）
  const showImportButton = actualPanelWidth === 0 ||
    actualPanelWidth >= HEADER_BASE_WIDTH + MENU_BUTTON_WIDTH + BUTTON_WIDTHS.import;

  const showUploadButton = actualPanelWidth === 0 ||
    actualPanelWidth >= HEADER_BASE_WIDTH + MENU_BUTTON_WIDTH + BUTTON_WIDTHS.import + BUTTON_WIDTHS.upload;

  const showBulkEditButton = actualPanelWidth === 0 ||
    actualPanelWidth >= HEADER_BASE_WIDTH + MENU_BUTTON_WIDTH + BUTTON_WIDTHS.import + BUTTON_WIDTHS.upload + BUTTON_WIDTHS.bulk;

  // 「スクリーンショットを取得」(§11.2.8) は常に三点メニューに入れるので、その画面では三点メニューを常に出す
  const menuAlwaysShown = showUrlCapture;
  const showTrashButton = actualPanelWidth >= HEADER_BASE_WIDTH + (menuAlwaysShown ? MENU_BUTTON_WIDTH : 0)
    + BUTTON_WIDTHS.import + BUTTON_WIDTHS.upload + BUTTON_WIDTHS.bulk + BUTTON_WIDTHS.trash;

  // 全部表示できて、常設の項目も無い時だけ三点メニューを隠す
  const showOverflowMenu = !showTrashButton || menuAlwaysShown;

  if (shouldHidePanel || (!isOpen && !isClosing)) return null;

  // 旧: view==='storyboard' のときは別レイアウトで絵コンテ画像を表示していた。
  // 現在は素材パネル内の 3rd タブ (storyboard) に統合済み。SimpleMaterialsList が
  // storyboardImageUrl != null の間だけタブを出して画像をレンダリングする。

  return (
    <>
      {/* モバイル: シート上端より上に露出する生成ワークスペース (モードタブ等) を
          ぼかすためのバックドロップ。タップでシートを閉じる (背後のタブ誤操作も防ぐ)。 */}
      {isMobile && (
        <div
          className="right-panel-backdrop"
          onClick={handleClose}
          aria-hidden="true"
        />
      )}
      {/* Panel */}
      <aside
        ref={handlePanelRef}
        className={`right-panel ${isOpen && !isClosing ? 'is-open' : ''} ${mode === 'manage' ? 'manage-mode' : ''} ${isResizing ? 'is-resizing' : ''} ${isSnapping ? 'is-snapping' : ''} ${isDragging ? 'is-dragging' : ''}`}
        style={{
          ...(isMobile ? undefined : { width: `${panelWidth}px` }),
          ...(isMobile && dragOffsetY > 0 ? { transform: `translateY(${dragOffsetY}px)` } : {}),
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* リサイズハンドル */}
        <div
          className="resize-handle"
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          aria-label="パネルの幅を調整"
        />
        {/* モバイル用ドラッグハンドル */}
        {isMobile && (
          <div
            className="right-panel-drag-handle"
            onTouchStart={handleSwipeStart}
            onTouchMove={handleSwipeMove}
            onTouchEnd={handleSwipeEnd}
          />
        )}
        <div
          className={`right-panel-header ${isMobile ? '' : 'right-panel-header-desktop'}`}
          {...(isMobile ? {
            onTouchStart: handleSwipeStart,
            onTouchMove: handleSwipeMove,
            onTouchEnd: handleSwipeEnd,
          } : {})}
        >
          {isMobile && (
            <button
              type="button"
              className={`right-panel-close ${selectedAssets.length > 0 ? 'with-text' : ''}`}
              onClick={handleClose}
              aria-label="閉じる"
            >
              {selectedAssets.length > 0 ? (
                <span className="close-button-text">閉じて生成へ</span>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              )}
            </button>
          )}
          {isMobile && <div className="right-panel-title"></div>}
          <div className="right-panel-header-actions">
            {isMobile ? (
              /* モバイル用レイアウト */
              <>
                {/* 素材取込。usodeki はドロップダウンにせず「画像アップロード」だけを直接表示 */}
                {simpleMaterialsUi ? (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={handleUploadPick}
                  >
                    <img src="/export_file.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    画像アップロード
                  </Button>
                ) : (
                <div className="menu-wrapper" ref={importMenuRef}>
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => setImportMenuOpen(!importMenuOpen)}
                  >
                    <img src="/upload_circle.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    {materialLabel}取込
                  </Button>
                  {importMenuOpen && (
                    <div className="dropdown">
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setImportMenuOpen(false);
                          setUploadModalOpen(true);
                        }}
                      >
                        <img src="/export_file.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} />
                        画像アップロード
                      </button>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setImportMenuOpen(false);
                          close();
                          setTimeout(() => {
                            hardNavigate('/products');
                          }, 100);
                        }}
                      >
                        <img src="/database_2.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} />
                        {brandConfig.labels.productShort}データ取込
                      </button>
                    </div>
                  )}
                </div>
                )}

                {/* usodeki の「画像アップロード」用の隠しファイル入力 (先にピッカーを開く) */}
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept={withSvgAccept('image/*,video/*', allowSvgUpload)}
                  style={{ display: 'none' }}
                  onChange={handleUploadInputChange}
                />

                {/* カメラ撮影ボタン（モバイル時のみ表示） */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={handleCameraCapture}
                />
                {isMobile && (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => cameraInputRef.current?.click()}
                    disabled={isCameraUploading}
                    loading={isCameraUploading}
                    aria-label="カメラで撮影"
                  >
                    <img src="/camera_light.svg" alt="" style={{ width: '16px', height: '16px' }} />
                  </Button>
                )}

                {/* 一括編集キャンセルボタン */}
                {bulkEditMode !== 'none' && (
                  <Button
                    type="button"
                    variant="muted"
                    size="md"
                    onClick={() => setBulkEditMode('none')}
                  >
                    キャンセル
                  </Button>
                )}

                {/* 三点メニュー */}
                <div className="menu-wrapper" ref={menuRef}>
                  <button
                    type="button"
                    className="menu-button"
                    onClick={() => setMenuOpen(!menuOpen)}
                    aria-label="メニュー"
                  >
                    <ThreeDotsIcon vertical />
                  </button>
                  {menuOpen && (
                    <div className="dropdown">
                      {/* usodeki は一括編集を出さず「ごみ箱」のみ */}
                      {!simpleMaterialsUi && (
                        <>
                          <div className="dropdown-label">一括編集</div>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setMenuOpen(false);
                              setBulkEditMode('delete');
                            }}
                          >
                            {materialLabel}の削除
                          </button>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setMenuOpen(false);
                              setBulkEditMode('tags');
                            }}
                          >
                            タグの追加・削除
                          </button>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setMenuOpen(false);
                              setBulkEditMode('move');
                            }}
                          >
                            {activeTab === 'temporary' ? '共有フォルダに移動' : '個人フォルダに移動'}
                          </button>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setMenuOpen(false);
                              setBulkEditMode('product-link');
                            }}
                          >
                            {brandConfig.labels.productShort}の関連付け
                          </button>
                          <div className="dropdown-separator" />
                        </>
                      )}
                      {/* スクリーンショットを取得 (§11.2.8・一括編集グループの下・一括編集の末尾に区切り線あり) */}
                      {showUrlCapture && (
                        <>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setMenuOpen(false);
                              urlCapture.open();
                            }}
                          >
                            <img src="/browser_com.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} />
                            スクショ取得
                          </button>
                          <div className="dropdown-separator" />
                        </>
                      )}
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setMenuOpen(false);
                          hardNavigate('/trash');
                        }}
                      >
                        ごみ箱
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* デスクトップ用レイアウト */
              <>
                {/* 素材ページを開く */}
                <Tooltip content={`${brandConfig.labels.material}ページで開く`}>
                  <button
                    type="button"
                    className="right-panel-icon-button"
                    onClick={() => {
                      const target = activeTab === 'temporary' ? '/materials/personal' : '/materials/shared';
                      // 現在のフィルタ条件を素材ページへ引き継ぐ（hardNavigate を跨ぐため sessionStorage 経由）
                      const filters = materialsListRef.current?.getActiveFilters();
                      try {
                        if (filters) {
                          sessionStorage.setItem(MATERIALS_FILTER_CARRYOVER_KEY, JSON.stringify(filters));
                        } else {
                          sessionStorage.removeItem(MATERIALS_FILTER_CARRYOVER_KEY);
                        }
                      } catch { /* sessionStorage 非対応環境では引き継ぎなし */ }
                      close();
                      setTimeout(() => {
                        hardNavigate(target);
                      }, 100);
                    }}
                    aria-label={`${brandConfig.labels.material}ページで開く`}
                  >
                    <img src="/expand_2.svg" alt="" style={{ width: '16px', height: '16px' }} />
                  </button>
                </Tooltip>

                {/* 各ボタンを個別に表示制御 */}
                {showImportButton && (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => {
                      close();
                      // パネルを閉じるアニメーションのために少し遅延
                      setTimeout(() => {
                        hardNavigate('/products');
                      }, 100);
                    }}
                  >
                    <img src="/database_2.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    {brandConfig.labels.productShort}データ取込
                  </Button>
                )}

                {showUploadButton && (
                  <Button
                    type="button"
                    variant="primary"
                    size="md"
                    onClick={() => setUploadModalOpen(true)}
                  >
                    <img src="/export_file.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    {materialLabel}アップロード
                  </Button>
                )}

                {/* 一括編集ドロップダウン / キャンセル */}
                {bulkEditMode !== 'none' ? (
                  <Button
                    type="button"
                    variant="muted"
                    size="md"
                    onClick={() => setBulkEditMode('none')}
                  >
                    一括編集をキャンセル
                  </Button>
                ) : (
                  showBulkEditButton && (
                    <div className="menu-wrapper" ref={bulkEditMenuRef}>
                      <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => setBulkEditMenuOpen(!bulkEditMenuOpen)}
                      >
                        一括編集
                        <span style={{ marginLeft: '6px', fontSize: '10px' }}>▼</span>
                      </Button>
                      {bulkEditMenuOpen && (
                        <div className="dropdown">
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => { setBulkEditMenuOpen(false); setBulkEditMode('delete'); }}
                          >
                            {materialLabel}の削除
                          </button>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => { setBulkEditMenuOpen(false); setBulkEditMode('tags'); }}
                          >
                            タグの追加・削除
                          </button>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => { setBulkEditMenuOpen(false); setBulkEditMode('move'); }}
                          >
                            {activeTab === 'temporary' ? '共有フォルダに移動' : '個人フォルダに移動'}
                          </button>
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => { setBulkEditMenuOpen(false); setBulkEditMode('product-link'); }}
                          >
                            {brandConfig.labels.productShort}の関連付け
                          </button>
                        </div>
                      )}
                    </div>
                  )
                )}

                {showTrashButton && (
                  <Button
                    type="button"
                    variant="muted"
                    size="md"
                    onClick={() => hardNavigate('/trash')}
                  >
                    ごみ箱
                  </Button>
                )}

                {/* 三点メニュー（折り畳み時のみ） */}
                {showOverflowMenu && (
                  <div className="menu-wrapper" ref={menuRef}>
                    <button
                      type="button"
                      className="menu-button"
                      onClick={() => setMenuOpen(!menuOpen)}
                      aria-label="メニュー"
                    >
                      <ThreeDotsIcon vertical />
                    </button>
                    {menuOpen && (
                      <div className="dropdown">
                        {!showImportButton && (
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setMenuOpen(false);
                              close();
                              setTimeout(() => {
                                hardNavigate('/products');
                              }, 100);
                            }}
                          >
                            {brandConfig.labels.productShort}データ取込
                          </button>
                        )}
                        {!showUploadButton && (
                          <button
                            type="button"
                            className="dropdown-item"
                            onClick={() => {
                              setMenuOpen(false);
                              setUploadModalOpen(true);
                            }}
                          >
                            <img src="/export_file.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} />
                            {materialLabel}アップロード
                          </button>
                        )}
                        {!showBulkEditButton && bulkEditMode === 'none' && (
                          <>
                            {(!showImportButton || !showUploadButton) && <div className="dropdown-separator" />}
                            <div className="dropdown-label">一括編集</div>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => { setMenuOpen(false); setBulkEditMode('delete'); }}
                            >
                              {materialLabel}の削除
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => { setMenuOpen(false); setBulkEditMode('tags'); }}
                            >
                              タグの追加・削除
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => { setMenuOpen(false); setBulkEditMode('move'); }}
                            >
                              {activeTab === 'temporary' ? '共有フォルダに移動' : '個人フォルダに移動'}
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => { setMenuOpen(false); setBulkEditMode('product-link'); }}
                            >
                              {brandConfig.labels.productShort}の関連付け
                            </button>
                          </>
                        )}
                        {/* スクリーンショットを取得 (§11.2.8・常設・一括編集グループの下) */}
                        {showUrlCapture && (
                          <>
                            {(!showImportButton || !showUploadButton || (!showBulkEditButton && bulkEditMode === 'none')) && <div className="dropdown-separator" />}
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => {
                                setMenuOpen(false);
                                urlCapture.open();
                              }}
                            >
                              <img src="/browser_com.svg" alt="" style={{ width: '16px', height: '16px', marginRight: '8px', verticalAlign: 'middle' }} />
                              スクショ取得
                            </button>
                          </>
                        )}
                        {!showTrashButton && (
                          <>
                            <div className="dropdown-separator" />
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() => {
                                setMenuOpen(false);
                                hardNavigate('/trash');
                              }}
                            >
                              ごみ箱
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {!isMobile && (
            <button
              type="button"
              className="right-panel-close right-panel-close-end"
              onClick={handleClose}
              aria-label="閉じる"
            >
              <Tooltip content={`${brandConfig.labels.material}パネルを閉じる`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </Tooltip>
            </button>
          )}
        </div>

        <div className="right-panel-content">
          <SimpleMaterialsList
            ref={materialsListRef}
            mode={mode}
            selectedAsin={contextData?.targetAsin}
            selectedBrandId={contextData?.targetBrandId}
            onSelectMaterial={(material) => {
              // 案件画像はラスタ画像のみ。非ラスタ原本 (SVG) はラスタ版 (無ければ取得) を渡す
              // (docs/features/material-raster-rendition-plan.md §3.2)。失敗は理由を出して中断
              const onSelect = contextData?.onSelect;
              if (!onSelect) return;
              void ensureMaterialRasterUrl(material)
                .then((url) => {
                  if (!url) {
                    toast.error('この素材は画像として使用できません');
                    return;
                  }
                  // 素材の要約も渡す (案件画像側が SVG 原本を引き継ぐ判断に使う)
                  onSelect(url, {
                    id: material.id,
                    folderType: detectIsTemporary(material) ? 'temporary' : 'cloud',
                    contentType: (material.content_type as string | null) ?? null,
                  });
                  close();
                })
                .catch((error) => {
                  console.error('ラスタ版の取得に失敗:', error);
                  toast.error(rasterErrorMessage(error));
                });
            }}
            bulkEditMode={bulkEditMode}
            onBulkEditComplete={() => setBulkEditMode('none')}
            bulkActionsTarget={panelEl}
            selectedAssets={selectedAssets}
            onAddAsset={addAsset}
            onRemoveAsset={removeAsset}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            imageType={contextData?.targetSlot === 'main' ? 'main' : 'sub'}
          />
        </div>

        {/* Upload Modal */}
        <UploadMaterialModal
          isOpen={uploadModalOpen}
          onClose={() => { setUploadModalOpen(false); setPendingUploadFile(null); }}
          defaultDestination={activeTab === 'cloud' ? 'cloud' : 'temporary'}
          initialFile={pendingUploadFile}
        />
        {urlCapture.modals}
      </aside>
    </>
  );
}
